import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  TechnologyInfrastructureAsset,
  TechnologyInfrastructureAssetUpsertPayload,
  createTechnologyInfrastructureAsset,
  deleteTechnologyInfrastructureAsset,
  listTechnologyInfrastructureAssets,
  updateTechnologyInfrastructureAsset,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

const INFRASTRUCTURE_CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'Real', symbol: 'R$' },
  { value: 'EUR', label: 'Euro', symbol: '€' },
  { value: 'USD', label: 'Dólar', symbol: 'US$' },
];

function currencyOption(value?: string | null) {
  if (!value?.trim()) return undefined;
  return INFRASTRUCTURE_CURRENCY_OPTIONS.find((option) => option.value === value);
}

function currencyLabel(value?: string | null): string {
  const option = currencyOption(value);
  if (option) return `${option.symbol} ${option.label}`;
  return value?.trim() || '—';
}

function formatMonthlyCost(amount?: number | null, currency?: string | null): string {
  if (amount == null) return '—';
  const formatted = amount.toLocaleString('pt-BR');
  const symbol = currencyOption(currency)?.symbol;
  return symbol ? `${symbol} ${formatted}` : formatted;
}

const emptyForm: TechnologyInfrastructureAssetUpsertPayload = {
  name: '',
  kind: 'Server',
  vendor: '',
  ipAddress: '',
  operatingSystem: '',
  cpuCores: null,
  ramGb: null,
  diskGb: null,
  region: '',
  currency: '',
  monthlyCost: null,
  notes: '',
};

function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

function formatOptionalNumber(value: number | null | undefined): string {
  if (value == null) {
    return '';
  }

  return String(value);
}

export function TechnologyInfrastructurePage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.infrastructure');
  const [items, setItems] = useState<TechnologyInfrastructureAsset[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyInfrastructureAssetUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyInfrastructureAssets({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        kind: kindFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar ativos de infraestrutura.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, kindFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyInfrastructureAsset): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      kind: item.kind,
      vendor: item.vendor ?? '',
      ipAddress: item.ipAddress ?? '',
      operatingSystem: item.operatingSystem ?? '',
      cpuCores: item.cpuCores ?? null,
      ramGb: item.ramGb ?? null,
      diskGb: item.diskGb ?? null,
      region: item.region ?? '',
      currency: item.currency ?? '',
      monthlyCost: item.monthlyCost ?? null,
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyInfrastructureAssetUpsertPayload = {
      ...form,
      vendor: form.vendor?.trim() || null,
      ipAddress: form.ipAddress?.trim() || null,
      operatingSystem: form.operatingSystem?.trim() || null,
      cpuCores: form.cpuCores,
      ramGb: form.ramGb,
      diskGb: form.diskGb,
      region: form.region?.trim() || null,
      currency: form.currency?.trim().toUpperCase() || null,
      monthlyCost: form.monthlyCost,
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyInfrastructureAsset(editingId, payload);
        toast.success('Ativo atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyInfrastructureAsset(payload);
        toast.success('Ativo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar ativo de infraestrutura.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyInfrastructureAsset): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir ativo',
      message: `Deseja excluir "${item.name}" do inventário de infraestrutura?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyInfrastructureAsset(item.id);
      toast.success('Ativo excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir ativo.');
    }
  }

  const selectedCurrency = currencyOption(form.currency);

  return (
    <PageShell>
      <PageHeader
        title="Infraestrutura"
        description="Inventário de servidores, cloud, bancos, storage, CDN e balanceadores."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar ativo' : 'Novo ativo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'ativo selecionado'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e cadastrar um ativo"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  {TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Fornecedor
                <input
                  className="erp-input"
                  value={form.vendor ?? ''}
                  onChange={(event) => setForm({ ...form, vendor: event.target.value })}
                />
              </label>
              <label className="erp-label">
                IP
                <input
                  className="erp-input"
                  value={form.ipAddress ?? ''}
                  onChange={(event) => setForm({ ...form, ipAddress: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Sistema operacional
                <input
                  className="erp-input"
                  value={form.operatingSystem ?? ''}
                  onChange={(event) => setForm({ ...form, operatingSystem: event.target.value })}
                />
              </label>
              <label className="erp-label">
                CPU (núcleos)
                <input
                  className="erp-input"
                  type="number"
                  min={1}
                  step={1}
                  value={formatOptionalNumber(form.cpuCores)}
                  onChange={(event) =>
                    setForm({ ...form, cpuCores: parseOptionalNumber(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                RAM (GB)
                <input
                  className="erp-input"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formatOptionalNumber(form.ramGb)}
                  onChange={(event) =>
                    setForm({ ...form, ramGb: parseOptionalNumber(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                Disco (GB)
                <input
                  className="erp-input"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formatOptionalNumber(form.diskGb)}
                  onChange={(event) =>
                    setForm({ ...form, diskGb: parseOptionalNumber(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label sm:col-span-2">
                <FieldLabel
                  description="Local ou região de nuvem em que este recurso está."
                  example="sa-east-1"
                >
                  Região
                </FieldLabel>
                <input
                  className="erp-input"
                  value={form.region ?? ''}
                  onChange={(event) => setForm({ ...form, region: event.target.value })}
                />
              </label>
              <div className="grid gap-4 sm:col-span-2 sm:grid-cols-2">
                <label className="erp-label">
                  <FieldLabel
                    description="Moeda em que o custo mensal é cobrado."
                    example="R$"
                  >
                    Moeda
                  </FieldLabel>
                  <select
                    className="erp-input"
                    value={form.currency ?? ''}
                    onChange={(event) => setForm({ ...form, currency: event.target.value })}
                  >
                    <option value="">Selecione</option>
                    {INFRASTRUCTURE_CURRENCY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.symbol} {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Custo mensal
                  {selectedCurrency ? (
                    <div className="mt-1 flex overflow-hidden rounded-lg border border-secondary-300 bg-white">
                      <span className="inline-flex items-center border-r border-secondary-300 bg-secondary-50 px-3 text-sm text-secondary-600">
                        {selectedCurrency.symbol}
                      </span>
                      <input
                        className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 focus:outline-none focus:ring-0"
                        type="number"
                        min={0}
                        step={0.01}
                        value={formatOptionalNumber(form.monthlyCost)}
                        onChange={(event) =>
                          setForm({ ...form, monthlyCost: parseOptionalNumber(event.target.value) })
                        }
                      />
                    </div>
                  ) : (
                    <input
                      className="erp-input"
                      type="number"
                      min={0}
                      step={0.01}
                      value={formatOptionalNumber(form.monthlyCost)}
                      onChange={(event) =>
                        setForm({ ...form, monthlyCost: parseOptionalNumber(event.target.value) })
                      }
                    />
                  )}
                </label>
              </div>
            </div>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome, IP, fornecedor ou região"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[220px]"
          value={kindFilter}
          onChange={(event) => {
            setPage(1);
            setKindFilter(event.target.value);
          }}
        >
          <option value="">Todos os tipos</option>
          {TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Fornecedor</th>
                  <th>IP</th>
                  <th>SO</th>
                  <th>CPU</th>
                  <th>RAM</th>
                  <th>Disco</th>
                  <th>Região</th>
                  <th>Moeda</th>
                  <th>Custo</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 12 : 11} className="text-center text-secondary-500">
                      Nenhum ativo cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS, item.kind)}
                      </td>
                      <td>{item.vendor || '—'}</td>
                      <td>{item.ipAddress || '—'}</td>
                      <td>{item.operatingSystem || '—'}</td>
                      <td>{item.cpuCores ?? '—'}</td>
                      <td>{item.ramGb != null ? `${item.ramGb} GB` : '—'}</td>
                      <td>{item.diskGb != null ? `${item.diskGb} GB` : '—'}</td>
                      <td>{item.region || '—'}</td>
                      <td>{currencyLabel(item.currency)}</td>
                      <td>{formatMonthlyCost(item.monthlyCost, item.currency)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
